import { Link } from "react-router-dom";
import { CheckCircle2, GraduationCap, Award, HeartHandshake, ArrowRight } from "lucide-react";
import { SITE, WHY_US } from "@/lib/site";
import { useSEO } from "@/lib/seo";
import SectionHeading from "@/components/SectionHeading";
import ClinicGallery from "@/components/ClinicGallery";

export default function About() {
  useSEO({
    title: `About ${SITE.doctor}`,
    description: `Meet ${SITE.doctor} — ${SITE.role} in Kothrud, Pune. Learn about her philosophy, credentials and approach to women's health.`,
    path: "/about",
  });

  return (
    <>
      <section className="pt-32 pb-16 lotus-bg" data-testid="about-hero">
        <div className="na-container grid gap-12 lg:grid-cols-2 items-center">
          <div className="fade-up">
            <span className="eyebrow">About Doctor</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Meet <span className="italic text-[color:var(--na-maroon)]">{SITE.doctor}</span>
            </h1>
            <p className="mt-4 text-[color:var(--na-teal)] font-semibold">{SITE.credentials}</p>
            <p className="text-sm uppercase tracking-widest text-[color:var(--na-muted)]">{SITE.role}</p>
            <p className="mt-6 text-lg text-[color:var(--na-muted)] leading-relaxed">
              Dedicated to helping women achieve hormonal balance, reproductive wellness, and healthy pregnancies through authentic Ayurveda and personalized care.
            </p>
          </div>
          <div className="fade-up-delay-2">
            <img src={SITE.doctorPhoto} alt={SITE.doctor} className="rounded-[2rem] shadow-xl w-full h-[520px] object-cover object-top" data-testid="about-photo" />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="na-container grid gap-10 lg:grid-cols-3">
          {[
            { icon: GraduationCap, title: "MS in Ayurveda", desc: "Master of Surgery in Obstetrics & Gynecology from a recognized Ayurvedic institute." },
            { icon: Award, title: "10+ Years Practice", desc: "Thousands of women guided through fertility, PCOS, pregnancy and menopause." },
            { icon: HeartHandshake, title: "Personalized Care", desc: "Every prakriti is unique — every treatment plan is built around you." },
          ].map((c) => (
            <div key={c.title} className="na-card p-7">
              <div className="w-12 h-12 rounded-xl bg-[color:var(--na-teal)]/10 text-[color:var(--na-teal)] flex items-center justify-center">
                <c.icon size={24} />
              </div>
              <h3 className="mt-4 text-2xl">{c.title}</h3>
              <p className="mt-2 text-sm text-[color:var(--na-muted)] leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-alt">
        <div className="na-container">
          <SectionHeading eyebrow="Why Choose Us" title="A rare blend of tradition and evidence" align="center" />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_US.map((w) => (
              <div key={w.title} className="na-card p-6 flex gap-4">
                <CheckCircle2 size={22} className="text-[color:var(--na-teal)] shrink-0 mt-1" />
                <div>
                  <p className="font-semibold">{w.title}</p>
                  <p className="mt-1 text-sm text-[color:var(--na-muted)] leading-relaxed">{w.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="na-container">
          <div className="na-card p-10 md:p-14 grid gap-6 lg:grid-cols-[1.4fr_1fr] items-center">
            <div>
              <h2 className="text-3xl md:text-4xl">Ready to take the next step?</h2>
              <p className="mt-3 text-[color:var(--na-muted)]">Book a consultation with Dr. Anita — in-person or online.</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link to="/book" className="btn-primary">Book Appointment <ArrowRight size={16} /></Link>
              <Link to="/treatments" className="btn-outline">Explore Treatments</Link>
            </div>
          </div>
        </div>
      </section>

      <ClinicGallery />
    </>
  );
}
